// Import Dependencies
import { useEffect, useReducer, useState } from "react";
import PropTypes from "prop-types";

// Local Imports
// import axios from "utils/axios";
import { isTokenValid, setSession } from "utils/jwt";
import { storeEncryptedPermissions, clearPermissions, getAllPermissions } from "utils/permissionsUtils";
import { getUserIdFromToken } from "utils/tokenUtils";
import { AuthContext } from "./context";
import { VAPID_KEY, JWT_HOST_API } from "configs/auth.config";
import { AUTH_DEV_FALLBACK, AUTH_DEV_TOKEN_TTL_MIN } from "configs/auth.config";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "firebase/firebaseConfig.js";
import axiosInstance from "utils/axios";
import { toast } from "sonner";
import signalRNotificationService from "services/signalrNotificationService";
import RolePermissionUpdateModal from "components/global/RolePermissionUpdateModal";
import { getUserById } from "services/authService";

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  errorMessage: null,
  user: null,
  companyConfig: null,
};

const reducerHandlers = {
  UPDATE_COMPANY_CONFIG: (state, action) => {
    return {
      ...state,
      companyConfig: action.payload
    };
  },
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },

  LOGIN_REQUEST: (state) => {
    return {
      ...state,
      isLoading: true,
      errorMessage: null, // Clear previous error messages
    };
  },

  LOGIN_SUCCESS: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      errorMessage: null, // Clear error on success
      user,
    };
  },

  LOGIN_ERROR: (state, action) => {
    const { errorMessage } = action.payload;

    return {
      ...state,
      errorMessage,
      isLoading: false,
    };
  },

  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  UPDATE_USER: (state, action) => {
    return {
      ...state,
      user: {
        ...action.payload,
      },
    };
  },

};

const reducer = (state, action) => {
  const handler = reducerHandlers[action.type];
  if (handler) {
    return handler(state, action);
  }
  return state;
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [rolePermissionModal, setRolePermissionModal] = useState({
    isOpen: false,
    message: '',
    countdownSeconds: 10
  });
  const notificationAccess = typeof Notification !== "undefined" ? Notification.permission : "default";
  async function registerToken(deviceToken) {
    try {
      await axiosInstance.post(`/api/Notification/Register-device`, {
        deviceToken,
      }, {
        showLoader: false
      });

    } catch (error) {
      console.error(error.message);
    }


  }
  async function unregisterToken(deviceToken) {
    try {
      await axiosInstance.delete(`/api/Notification/Remove-DeviceToken?token=${deviceToken}`, {
        showLoader: false
      });

    } catch (error) {
      console.error(error.message);
    }


  }
  const requestNotificationPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Browser notifications are not supported in this environment.");
      return "unsupported";
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
        });

        if (token) {
          await registerToken(token);
          localStorage.setItem("deviceToken", token);
        }
      }
      // } else if (permission === "denied") {
      //   toast.error("Notification permission denied by the user");
      // }

      return permission;
    } catch (error) {
      console.error("Notification permission error:", error);
      return "default";
    }
  };

  useEffect(() => {
    if (state.isAuthenticated) {
      requestNotificationPermission();
    }
  }, [state.isAuthenticated])
  const loadUserData = async () => {
    try {
      const userId = getUserIdFromToken();
      if (!userId) return;

      const response = await getUserById(userId);

      if (response.success && response.data) {
        //🔥 Update AuthContext state.user
        dispatch({
          type: "UPDATE_USER",
          payload: response.data,
        });
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };
  // Fetch company config
  useEffect(() => {
    const setCompanyConfigFromUrl = () => {
      try {
        const hostname = window.location.hostname;
        let folderName = hostname;

        // Handle localhost development environment
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
          folderName = 'localhost';
        }

        // Ensure trailing slash on API host
        const apiHost = JWT_HOST_API.endsWith('/') ? JWT_HOST_API : `${JWT_HOST_API}/`;
        const logosBase = `${apiHost}CompanyLogos/${folderName}`;

        const config = {
          LogoUrl: `${logosBase}/main.png`,
          MiniLogoUrl: `${logosBase}/mini.png`,
          FaviconUrl: `${logosBase}/favicon.ico`
        };

        dispatch({
          type: "UPDATE_COMPANY_CONFIG",
          payload: config,
        });

        // Set Favicon using FaviconUrl with fallback check
        if (config.FaviconUrl) {
          const img = new Image();
          img.src = config.FaviconUrl;
          img.onload = () => {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = config.FaviconUrl;

            // Update type based on extension
            if (config.FaviconUrl.endsWith('.png')) {
              link.type = 'image/png';
            } else if (config.FaviconUrl.endsWith('.ico')) {
              link.type = 'image/x-icon';
            } else if (config.FaviconUrl.endsWith('.svg')) {
              link.type = 'image/svg+xml';
            } else {
              link.removeAttribute('type');
            }
          };
          img.onerror = () => {
            // Fallback handled by keeping existing favicon or doing nothing
            console.warn("Failed to load dynamic favicon, using default.");
          };
        }
      } catch (error) {
        console.error("Failed to set company config from URL", error);
      }
    };
    setCompanyConfigFromUrl();

    if (state.isAuthenticated) {
      loadUserData();
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    if (notificationAccess === "default" || notificationAccess === "denied") {
      const token = localStorage.getItem("deviceToken");
      if (token) {
        unregisterToken(token);
        localStorage.removeItem("deviceToken");
      }
    }

  }, [notificationAccess]);


  useEffect(() => {
    const init = async () => {
      try {
        // Check for existing token in localStorage
        const storedToken = localStorage.getItem("authToken");

        if (storedToken && isTokenValid(storedToken)) {

          // Valid token exists, extract user data from token
          const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
          const mockUser = {
            id: parseInt(tokenPayload.sub || "1"),
            name: tokenPayload.name || "Demo User",
            email: tokenPayload.email || "demo@example.com",
            username: tokenPayload.username || tokenPayload.email?.split('@')[0] || "demo",
            avatar: tokenPayload.avatar || null,
            role: tokenPayload.role || "user",
          };

          // Set the session with existing token
          setSession(storedToken);

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user: mockUser,
            },
          });

          // Start SignalR connection after successful initialization
          setTimeout(async () => {
            try {
              console.info('Starting SignalR connection after initialization...');
              await signalRNotificationService.startConnection(storedToken);
              console.info('✅ SignalR connected successfully after initialization');
            } catch (signalRError) {
              console.error('Failed to connect SignalR after initialization:', signalRError);
            }
          }, 100);
        } else {
          // No valid token, user needs to login
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        // Initialize as unauthenticated on error
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    init();
  }, []);

  // SignalR subscription for role permission updates
  useEffect(() => {
    if (state.isAuthenticated) {
      // Check for existing role permission update on mount
      const storedModalData = localStorage.getItem('rolePermissionUpdate');
      if (storedModalData) {
        try {
          const modalData = JSON.parse(storedModalData);
          const currentTime = Date.now();

          // Only show if less than 30 seconds have passed since the update
          if (currentTime - modalData.timestamp < 30000) {
            setRolePermissionModal({
              isOpen: true,
              message: modalData.message || 'Permissions for your role have been changed',
              countdownSeconds: Math.max(1, Math.floor((30000 - (currentTime - modalData.timestamp)) / 1000))
            });
          } else {
            // Clear expired data
            localStorage.removeItem('rolePermissionUpdate');
          }
        } catch (error) {
          console.error('Error parsing stored modal data:', error);
          localStorage.removeItem('rolePermissionUpdate');
        }
      }

      // Check SignalR connection health
      const checkAndSubscribe = async () => {
        console.info('🔍 Checking SignalR connection health...');
        const isHealthy = await signalRNotificationService.checkConnectionHealth();
        console.info('SignalR connection health:', isHealthy);

        if (isHealthy) {
          console.info('✅ SignalR is healthy, subscribing to role permission updates...');
        } else {
          console.warn('⚠️ SignalR connection issues detected');
        }
      };

      // Subscribe to role permission updates
      const subscriptionId = signalRNotificationService.subscribe(
        'UPDATE_ROLE_PERMISSION',
        (notification) => {
          console.info('🔐 Role permission update received in auth provider:', notification);

          // Store in localStorage for persistence
          const modalData = {
            message: notification.message || 'Permissions for your role have been changed',
            timestamp: Date.now(),
            countdownSeconds: notification.countdownSeconds || 10
          };
          localStorage.setItem('rolePermissionUpdate', JSON.stringify(modalData));

          // Show the modal
          setRolePermissionModal({
            isOpen: true,
            message: modalData.message,
            countdownSeconds: modalData.countdownSeconds
          });
        }
      );

      console.info('📝 Subscribed to UPDATE_ROLE_PERMISSION events with ID:', subscriptionId);
      // Check connection health and setup periodic checks
      checkAndSubscribe();
      const healthCheckInterval = setInterval(() => {
        checkAndSubscribe();
      }, 30000); // Check every 30 seconds

      // Cleanup subscription when unmounting or when authentication state changes
      return () => {
        console.info('🗑️ Cleaning up SignalR subscription:', subscriptionId);
        signalRNotificationService.unsubscribe(subscriptionId);
        clearInterval(healthCheckInterval);
      };
    }
  }, [state.isAuthenticated]);


  const login = async ({ username, email, password, token, userId, refreshToken, roles, userData, isNewUser }) => {
    dispatch({
      type: "LOGIN_REQUEST",
    });

    try {
      // Basic client-side checks
      let userEmail = email || username;

      // If email is not provided, try to extract from token
      if (!userEmail && token) {
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          userEmail = tokenPayload.email || tokenPayload.sub || username;
        } catch (e) {
          console.error("Failed to extract email from token:", e);
        }
      }

      if (!userEmail || !userEmail.trim()) {
        throw new Error("Email is required");
      }

      let authToken = token;
      let user;

      if (authToken) {
        // Use the token directly from login/password setup APIs
        // Extract user info from token payload or use provided userData
        try {
          const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
          user = {
            id: userId || tokenPayload.nameid || tokenPayload.sub,
            name: userData?.name || tokenPayload.name || userEmail.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            email: userEmail,
            username: userEmail.split('@')[0] || userEmail,
            avatar: null,
            role: roles || tokenPayload.role || "user",
            companyId: tokenPayload.CompanyId,
            refreshToken: refreshToken,
          };

        } catch (e) {
          console.error('Failed to parse token:', e);
          // Fallback user object
          user = {
            id: userId || "1",
            name: userEmail.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            email: userEmail,
            username: userEmail.split('@')[0] || userEmail,
            avatar: null,
            role: roles || "user",
            refreshToken: refreshToken,
          };
        }
      } else {
        // Fallback for development when no token is provided
        if (AUTH_DEV_FALLBACK) {
          const exp = Math.floor(Date.now() / 1000) + (AUTH_DEV_TOKEN_TTL_MIN * 60);
          const payload = btoa(JSON.stringify({ sub: "1", name: userEmail.split('@')[0], email: userEmail, exp }));
          authToken = `dev.${payload}.token`;

          user = {
            id: "1",
            name: userEmail.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            email: userEmail,
            username: userEmail.split('@')[0] || userEmail,
            avatar: null,
            role: "user",
          };
        } else {
          throw new Error('Authentication token is required');
        }
      }

      // Persist auth token for subsequent requests
      setSession(authToken);

      // Store encrypted permissions if available
      if (roles) {
        storeEncryptedPermissions(roles);
      }
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: {
          user,
        },
      });

      // Reconnect SignalR after successful login
      try {
        console.info('🔄 Reconnecting SignalR after login...');
        await signalRNotificationService.startConnection(authToken);
        console.info('✅ SignalR reconnected successfully after login');
      } catch (signalRError) {
        console.error('❌ Failed to reconnect SignalR after login:', signalRError);
        // Don't fail the login, just log the error
      }

    } catch (err) {
      console.error('Login error:', err);
      const message = err?.message || 'Invalid credentials';
      dispatch({
        type: "LOGIN_ERROR",
        payload: {
          errorMessage: { message },
        },
      });
    }
  };

  const logout = async () => {
    // Clear role permission modal data
    localStorage.removeItem('rolePermissionUpdate');

    // Stop SignalR connection
    try {
      console.info('🛑 Stopping SignalR connection during logout...');
      await signalRNotificationService.stopConnection();
      console.info('✅ SignalR connection stopped successfully');
    } catch (signalRError) {
      console.error('❌ Error stopping SignalR connection:', signalRError);
    }

    setSession(null);
    clearPermissions();
    dispatch({ type: "LOGOUT" });
  };

  const handleRolePermissionLogout = async () => {
    // Clear the modal data from localStorage
    localStorage.removeItem('rolePermissionUpdate');

    // Stop SignalR connection
    try {
      console.info('🛑 Stopping SignalR connection during role permission logout...');
      await signalRNotificationService.stopConnection();
      console.info('✅ SignalR connection stopped successfully');
    } catch (signalRError) {
      console.error('❌ Error stopping SignalR connection:', signalRError);
    }

    // Close the modal
    setRolePermissionModal({
      isOpen: false,
      message: '',
      countdownSeconds: 10
    });

    // Perform logout
    await logout();

    // Redirect to login page
    window.location.href = '/login';
  };

  if (!children) {
    return null;
  }

  return (
    <AuthContext
      value={{
        ...state,
        login,
        logout,
        loadUserData,
        requestNotificationPermission,
      }}
    >
      {children}
      <RolePermissionUpdateModal
        isOpen={rolePermissionModal.isOpen}
        message={rolePermissionModal.message}
        countdownSeconds={rolePermissionModal.countdownSeconds}
        onLogout={handleRolePermissionLogout}
      />
    </AuthContext>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
